import type { RequestOptions } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { equityMarketDataFetcher } from '@/libs/fetcher/presets'
import type { StockAssetDetail, StockMeta } from '@/types/stock'

export const fetchStockBySymbol = (
  symbol: string,
  requestOptions?: RequestOptions,
  silent404 = false,
) =>
  equityMarketDataFetcher<StockAssetDetail>(`/assets/${symbol}`, requestOptions, {
    customData: {
      silent404,
      enableDefaultNextjsServerFetchCache: true, // Cache for 1 minute (60 seconds)
    },
  })

export const assetDetailToMeta = (asset: StockAssetDetail): StockMeta => ({
  name: asset.name ?? '',
  slug: asset.slug,
  symbol: asset.symbol,
  active: asset.status === 'active',
  tags: [],
  description: asset.issuer_description ?? '',
  platform: [],
  icon: asset.logo ?? '',
  id: parseInt(asset.asset_id, 10) || 0,
  source_slug: asset.slug.toLowerCase(),

  website_urls: [],
  whitepaper_urls: [],
  whitepaper_urls_v2: [],
  explorer_urls: [],
  sourcecode_urls: [],
  message_board_urls: [],
  announcement_urls: [],
  chat_urls: [],
  telegram_urls: [],
  discord_urls: [],
  reddit_urls: [],
  twitter_urls: [],
  sustainability_disclosure_urls: [],
  rss_feeds: [],

  is_defi_swap: false,
  meta_title: `${asset.name ?? asset.symbol} Stock Price`,
  trading_view_symbol: asset.symbol,
  trading_view_status: 1,
  price_engine_enabled: true,
  visibility: 'public',

  exchange: undefined,
  country: asset.country ?? undefined,
  sector: asset.sector ?? undefined,
  industry: undefined,
})

export const fetchStockMetaBySymbol = async (
  symbol: string,
  requestOptions?: RequestOptions,
  silent404 = false,
): Promise<StockMeta | null> => {
  const assetDetail = await fetchStockBySymbol(symbol, requestOptions, silent404)

  if (!assetDetail) {
    return null
  }

  return assetDetailToMeta(assetDetail)
}

export const stockBySymbolQueryOptions = (symbol: string) =>
  queryOptions({
    queryKey: ['stockBySymbol', symbol],
    queryFn: () => fetchStockBySymbol(symbol, undefined, true),
  })

export const useStockBySymbol = (symbol: string) =>
  useSuspenseQuery(stockBySymbolQueryOptions(symbol))
